import { FilesRailDraggableFileRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailDraggableFileRow";
import type { FilesRailFileMenuActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailFileRow";
import { FilesRailFolderRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailFolderRow";
import type { FilesRailOsDropAtPath } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/useFilesRailWorkspaceFiles";
import { getDirectChildren } from "@/pages/workspaces/utils/folderPaths";
import { getFileRecordName } from "@/panes/file-record-detail/contentType";
import type { FileRecord } from "@cohesive/api-client";
import { useMemo } from "react";

function sortDirectChildren(records: FileRecord[], parentPath: string) {
  const children = getDirectChildren(records, parentPath).filter(
    (r) => r.status !== "pending" && r.status !== "deleted",
  );
  const folders = children.filter((r) => r.path.endsWith("/"));
  const files = children.filter((r) => !r.path.endsWith("/"));
  const byName = (a: FileRecord, b: FileRecord) =>
    getFileRecordName(a).localeCompare(getFileRecordName(b), undefined, {
      sensitivity: "base",
    });
  folders.sort(byName);
  files.sort(byName);
  return [...folders, ...files];
}

function dragNamespaceFor(workspaceId: string, parentPath: string) {
  return parentPath
    ? `files-rail:${workspaceId}:${parentPath}`
    : `files-rail:${workspaceId}:root`;
}

export function FilesRailTree({
  records,
  workspaceId,
  parentPath = "",
  activeFileRecordId,
  onOpenFile,
  menuActions,
  getOsDropHandler,
  onUploadToPath,
  onCreateDocumentAtPath,
  onCreateSubfolder,
}: {
  records: FileRecord[];
  workspaceId: string;
  parentPath?: string;
  activeFileRecordId: string | null;
  onOpenFile: (record: FileRecord) => void;
  menuActions?: FilesRailFileMenuActions;
  getOsDropHandler?: FilesRailOsDropAtPath;
  onUploadToPath?: (parentPath: string) => void;
  onCreateDocumentAtPath?: (parentPath: string) => void;
  onCreateSubfolder?: (parentPath: string) => void;
}) {
  const direct = useMemo(
    () => sortDirectChildren(records, parentPath),
    [records, parentPath],
  );

  if (direct.length === 0) {
    if (parentPath) {
      return <p className="px-2 py-1 text-sm text-n-40">No files in folder.</p>;
    }
    return null;
  }

  return (
    <>
      {direct.map((record) => {
        if (record.path.endsWith("/")) {
          return (
            <FilesRailFolderRow
              key={record.id}
              record={record}
              workspaceId={workspaceId}
              onOsDrop={getOsDropHandler?.(record.path) ?? null}
              menuActions={menuActions}
              onUploadToPath={onUploadToPath}
              onCreateDocumentAtPath={onCreateDocumentAtPath}
              onCreateSubfolder={onCreateSubfolder}
            >
              <FilesRailTree
                records={records}
                workspaceId={workspaceId}
                parentPath={record.path}
                activeFileRecordId={activeFileRecordId}
                onOpenFile={onOpenFile}
                menuActions={menuActions}
                getOsDropHandler={getOsDropHandler}
                onUploadToPath={onUploadToPath}
                onCreateDocumentAtPath={onCreateDocumentAtPath}
                onCreateSubfolder={onCreateSubfolder}
              />
            </FilesRailFolderRow>
          );
        }

        return (
          <FilesRailDraggableFileRow
            key={record.id}
            record={record}
            workspaceId={workspaceId}
            dragNamespace={dragNamespaceFor(workspaceId, parentPath)}
            active={activeFileRecordId === record.id}
            onOpen={() => onOpenFile(record)}
            menuActions={menuActions}
            // Phantom chevron at every level: file icons share the icon
            // column with expandable siblings, and each pl-2 wrapper reads
            // as the tree's uniform 8px step (see FilesRailFolderRow).
            withChevronSpacer
          />
        );
      })}
    </>
  );
}

/** Flat list of top-level rail rows for `RailGroup` pagination. */
export function useFilesRailRootItems({
  records,
  workspaceId,
  activeFileRecordId,
  onOpenFile,
  menuActions,
  getOsDropHandler,
  onUploadToPath,
  onCreateDocumentAtPath,
  onCreateSubfolder,
}: {
  records: FileRecord[] | undefined;
  workspaceId: string | undefined;
  activeFileRecordId: string | null;
  onOpenFile: (record: FileRecord) => void;
  menuActions?: FilesRailFileMenuActions;
  getOsDropHandler?: FilesRailOsDropAtPath;
  onUploadToPath?: (parentPath: string) => void;
  onCreateDocumentAtPath?: (parentPath: string) => void;
  onCreateSubfolder?: (parentPath: string) => void;
}) {
  return useMemo(() => {
    if (!workspaceId || !records) return [];
    if (records.length === 0) return [];
    return sortDirectChildren(records, "").map((record) => {
      if (record.path.endsWith("/")) {
        return (
          <FilesRailFolderRow
            key={record.id}
            record={record}
            workspaceId={workspaceId}
            onOsDrop={getOsDropHandler?.(record.path) ?? null}
            menuActions={menuActions}
            onUploadToPath={onUploadToPath}
            onCreateDocumentAtPath={onCreateDocumentAtPath}
            onCreateSubfolder={onCreateSubfolder}
          >
            <FilesRailTree
              records={records}
              workspaceId={workspaceId}
              parentPath={record.path}
              activeFileRecordId={activeFileRecordId}
              onOpenFile={onOpenFile}
              menuActions={menuActions}
              getOsDropHandler={getOsDropHandler}
              onUploadToPath={onUploadToPath}
              onCreateDocumentAtPath={onCreateDocumentAtPath}
              onCreateSubfolder={onCreateSubfolder}
            />
          </FilesRailFolderRow>
        );
      }

      return (
        <FilesRailDraggableFileRow
          key={record.id}
          record={record}
          workspaceId={workspaceId}
          dragNamespace={dragNamespaceFor(workspaceId, "")}
          active={activeFileRecordId === record.id}
          onOpen={() => onOpenFile(record)}
          menuActions={menuActions}
          withChevronSpacer
        />
      );
    });
  }, [
    records,
    workspaceId,
    activeFileRecordId,
    onOpenFile,
    menuActions,
    getOsDropHandler,
    onUploadToPath,
    onCreateDocumentAtPath,
    onCreateSubfolder,
  ]);
}
